<template>

    <BooklistEditor />
    <BooklistCancellationDialog />

    <PageSection fixed>
        <PageHeader id="page-header" title="Booklist">
            <Button id="download-btn" class="mr-4" @click="downloadBooklist" download :loading="isDownloadingTable" :disabled="isDownloadingTable || isBooklistLoading">
                Download this view
            </Button>
            <Button id="btn-request" @click="goToRequest" :disabled="isBooklistLoading">
                Make a request
            </Button>
            <template #alert>
                <Label v-show="titleRequestStore.showMessage && !showRequestLabelTimerReady">
                    Upload Saved
                </Label>
            </template>
        </PageHeader>
        <Card>
            <TableLayout>
                <template #toolbar>
                    <div class="table-toolbar b-0">
                        <BulkSelectToolbar :table="table" v-if="hasEditPemission">
                            <Tooltip v-if="selectedRows.length === 1 && metaServiceEnabled" position="top"
                                :enabled="disableDownloadWayflessOrMARC(selectedRows[0], 'MARC')">
                                <button :disabled="disableDownloadWayflessOrMARC(selectedRows[0], 'MARC')"
                                    @click="handleDownloadMARCRecord(selectedRows[0])"
                                    class="btn-table-action flex items-center transition gap-1 cursor-pointer">
                                    <DownloadIcon />
                                    <span>Download MARC</span>
                                </button>
                                <template #tooltip-content>
                                    <p>{{ getToolTipMessage(selectedRows[0].customer_status, selectedRows[0]?.marcStatus,
                                        'MARC record') }}</p>
                                </template>
                            </Tooltip>
                            <Tooltip v-if="selectedRows.length === 1 && metaServiceEnabled" position="top"
                                :enabled="disableDownloadWayflessOrMARC(selectedRows[0], 'WAYFless')">
                                <button :disabled="disableDownloadWayflessOrMARC(selectedRows[0], 'WAYFless')"
                                    @click="handleDownloadWayflessUrl(selectedRows[0])"
                                    class="btn-table-action flex items-center transition gap-1 cursor-pointer">
                                    <CopyIcon />
                                    <span>Copy WAYFless</span>
                                </button>
                                <template #tooltip-content>
                                    <p>{{ getToolTipMessage(selectedRows[0].customer_status,
                                        selectedRows[0]?.wayfless?.status, 'WAYFless URL') }}</p>
                                </template>
                            </Tooltip>
            
                            <button @click="openEditDialog(selectedRows)" class="btn-table-action">
                                Edit
                            </button>
                            <button @click="openCancelDialog(selectedRows.map(row => row.id))" class="btn-table-action">
                                Cancel
                            </button>
                        </BulkSelectToolbar>
            
                        <SearchBar placeholder="Search ISBN or title" @search="fetch({ searchText: $event })"
                            :initial-search="searchQuery" />
                        <div class="flex-row gap-2 px-1.5">
                            <FilterSet :filters="filterableColumns" @filters-updated="handleUpdatedFilters"
                                :initialFilters="initialFilters" />
                            <DateFilterComponent :selectedDateAfter="selectedDateAfter"
                                :selectedDateRange="selectedDateRange" v-model:multi-calendars="isMultiCalendars"
                                @update:selectedDateAfter="handleDateRangeChanged"
                                @update:selectedDateRange="handleDateRangeChanged" />
                            <ColumnVisibility :table="table" />
                        </div>
                    </div>
                </template>
            
                <Table :table="table" :show-loading-spinner="isBooklistLoading" :skeleton-row-count="25" aria-describedby="page-header" class="b-l-style-none b-r-style-none b-b-style-solid">
                    <thead class="table-thead">
                        <tr class="flex" v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
                            <TableHeaderCell v-for="header in headerGroup.headers" :header="header">
                                <template #selector>
                                    <RowSelector :row="table"></RowSelector>
                                </template>
                            </TableHeaderCell>
                        </tr>
                    </thead>
                    <tbody class="grid">
                        <tr class="table-row" v-for="row in table.getCoreRowModel().rows" :key="row.id">
            
                            <!-- NOTE: this component is where all the cell rendering happens. If there isn't a template
                                below then the cell will render the default cell content. -->
                            <TableDataCell v-for="cell in row.getVisibleCells()" :cell="cell">
            
                                <template #selector>
                                    <RowSelector :row="row"></RowSelector>
                                </template>
            
                                <template #ops_fields.unit_sale_amount>
                                    <Price
                                        v-if="cell.row.original.ops_fields?.unit_sale_amount && cell.row.original.ops_fields?.currency"
                                        :currency="cell.row.original.ops_fields?.currency"
                                        :amount="cell.row.original.ops_fields?.unit_sale_amount" />
                                    <span v-else></span>
                                </template>
            
                                <template #item.access_length>
                                    <span :style="{ color: cell.row.original.ops_fields?.confirmed_access_length && cell.row.original.item.access_length && cell.row.original.ops_fields.confirmed_access_length !== cell.row.original.item.access_length ? '#A56200' : undefined }">
                                        {{ cell.row.original.ops_fields?.confirmed_access_length ?? cell.row.original.item.access_length }}
                                    </span>
                                    <span v-if="cell.row.original.ops_fields?.confirmed_access_length && cell.row.original.item.access_length && cell.row.original.ops_fields.confirmed_access_length !== cell.row.original.item.access_length" class="ml-1 inline-flex items-center">
                                        <Tooltip position="bottom">
                                            <InfoIconSVG class="w-4 h-4 info-icon-warning" style="vertical-align: middle; position: relative; top: 2px;" />
                                            <template #tooltip-content>
                                                <div class="p-3 max-w-xs">
                                                    <p class="font-bold">Updated Access Length:</p>
                                                    <p class="font-normal">You requested this title as <span class="font-bold">{{ cell.row.original.item.access_length }}</span> access length, but the confirmed access length for this title is <span class="font-bold">{{ cell.row.original.ops_fields.confirmed_access_length }}</span>.</p>
                                                </div>
                                            </template>
                                        </Tooltip>
                                    </span>
                                </template>
            
                                <template #customer_status>
                                    <Tooltip position="left" :enabled="isStatusTooltipEnabled(cell.row.original)">
                                        <Tag :variant="getCustomerStatusChipType(getApprovalStatus(cell.row.original))"
                                            :clearable="false">
                                            {{ getStatusChipText(cell.row.original) }}
                                        </Tag>
                                        <template #tooltip-content>
                                            <p>{{ getTooltipContent(cell.row.original) }}</p>
                                        </template>
                                    </Tooltip>
                                </template>
            
                                <template #item_state.value>
                                    <Tooltip position="left" :enabled="isStatusTooltipEnabled(cell.row.original)">
                                        <Tag :variant="getCustomerStatusChipType(getApprovalStatus(cell.row.original))"
                                            :clearable="false">
                                            {{ getStatusChipText(cell.row.original) }}
                                        </Tag>
                                        <template #tooltip-content>
                                            <p>{{ getTooltipContent(cell.row.original) }}</p>
                                        </template>
                                    </Tooltip>
                                </template>
                                <template #actions>
                                    <Menu placement="auto" :items="getRowActions(cell)" class="cell-menu" is-icon />
                                </template>
            
                            </TableDataCell>
                        </tr>
                    </tbody>
            
                    <template #empty>
                        <EmptyState :icon="message.icon" :title="message.title" :text="message.description">
                            <router-link v-if="!message.error" to="/booklist/upload" class="table-btn-primary">
                                Go to upload page
                            </router-link>
                        </EmptyState>
                    </template>
                </Table>
            
                <template #paginator>
                    <Paginator :table="table" :page-sizes="[5, 10, 25, 50, 100, 200]" :loading="isBooklistLoading" class="b-0"/>
                </template>
            </TableLayout>
        </Card>
    </PageSection>
</template>

<script lang="ts" setup>
import {
	Cell,
	functionalUpdate,
	getCoreRowModel,
	useVueTable,
} from "@tanstack/vue-table";
import { useTimeout } from "@vueuse/core";
import { BooklistSearchSchemaInput } from "core-services/stacks/institutions/substacks/booklist/lib/repositories/booklist-row-confirmed-repository";
import { match, P } from "ts-pattern";
import { computed, watch } from "vue";
import { useRouter } from "vue-router";
import { definePage } from "vue-router/auto";
import CopyIcon from "../../../../shared/assets/icons/copy.svg?component";
import BulkSelectToolbar from "../../../../shared/components/ComposableTable/BulkSelectToolbar.vue";
import ColumnVisibility from "../../../../shared/components/ComposableTable/ColumnVisibility.vue";
import Paginator from "../../../../shared/components/ComposableTable/Paginator.vue";
import RowSelector from "../../../../shared/components/ComposableTable/RowSelector.vue";
import Table from "../../../../shared/components/ComposableTable/Table.vue";
import TableDataCell from "../../../../shared/components/ComposableTable/TableDataCell.vue";
import TableHeaderCell from "../../../../shared/components/ComposableTable/TableHeaderCell.vue";
import TableLayout from "../../../../shared/components/ComposableTable/TableLayout.vue";
import { useGuardedTableColumns } from "../../../../shared/components/ComposableTable/useGuardedTableColumns";
import EmptyState from "../../../../shared/components/EmptyState.vue";
import {
	Filter,
	SelectedFilter,
} from "../../../../shared/components/Filters/Filter.vue";
import FilterSet from "../../../../shared/components/Filters/FilterSet.vue";
import SearchBar from "../../../../shared/components/Headers/SearchBar.vue";
import Menu, { MenuItemData } from "../../../../shared/components/Menu.vue";
import PageSection from "../../../../shared/components/PageSection.vue";
import Price from "../../../../shared/components/Price.vue";
import Tag from "../../../../shared/components/Tag.vue";
import Tooltip from "../../../../shared/components/Tooltip.vue";
import { useTableFilters } from "../../../../shared/composables/useTableFilters";
import { isFeatureFlagEnabled } from "../../../../shared/lib/posthog";
import DownloadIcon from "../../assets/icons/download.svg?component";
import InfoIconSVG from "../../assets/icons/info.svg?component";
import BooklistCancellationDialog from "../../components/Booklist/BooklistCancellationDialog.vue";
import BooklistEditor from "../../components/Booklist/BooklistEditor.vue";
import DateFilterComponent from "../../components/Filter/DateFilterComponent.vue";
import Label from "../../components/Label.vue";
import PageHeader from "../../components/PageHeader.vue";
import { useBooklist } from "../../composables/useBooklist";
import { useInstitution } from "../../composables/useInstitution";
import { useMetaService } from "../../composables/useMetaService";
import { usePermission } from "../../composables/usePermission";
import { getStatusChipType as getCustomerStatusChipType } from "../../config";
import { pubSubClient } from "../../pub-sub/pub-sub-client";
import { BooklistRow } from "../../state-machines/booklist-table-actor";
import { toastMessageActorV5 } from "../../state-machines/toast-messages-actor-v5";
import { useAcademicDates } from "../../stores/academic-dates";
import { useIndividualTitleRequest } from "../../stores/individual-title-request";
import { trpc } from "../../trpc/client";
import { generateTableColumns } from "../../utils/booklist-table-columns";
import { getRequestPageRoute } from "../../utils/request-page-route";

const {
	institutionConfig,
	isEAorIA,
	isAccessProgramEnabled,
	isRequirementStatusEnabled,
} = useInstitution();
const { hasPermission, permissions } = usePermission();
const hasEditPemission = computed(() =>
	hasPermission(permissions.value, "booklist:edit"),
);
const isDownloadingTable = ref(false);

// The state machine for the table.
const {
	fetch,
	selectRows,
	openCancelDialog,
	openEditDialog,
	setColumnVisibility,
	setColumnOrder,
	isBooklistLoading,
	message,
	rows: data,
	selectedRows,
	totalRowCount: rowCount,
	searchQuery,
	filters,
	columnOrder,
	columnPinning,
	columnVisibility,
	pagination,
	rowSelection,
	sorting,
	columns: booklistColumns,
} = useBooklist({
	resetSelection() {
		table.resetRowSelection();
	},
});

// The columns we want to display in the table.
const columns = useGuardedTableColumns<BooklistRow>((helper) => [
	{
		guard: () => hasEditPemission.value,
		columnDef: helper.display({
			id: "selector",
			size: 48,
			enableHiding: false,
			enableSorting: false,
			enableResizing: false,
		}),
	},
	...generateTableColumns(booklistColumns.value, helper),
	{
		guard: () => hasEditPemission.value,
		columnDef: helper.display({
			id: "actions",
			header: "Actions",
			size: 72,
			enableSorting: false,
			enableResizing: false,
			enableHiding: false,
		}),
	},
]);

const table = useVueTable<BooklistRow>({
	getCoreRowModel: getCoreRowModel<BooklistRow>(),
	manualPagination: true,
	columnResizeMode: "onChange",
	getRowId: (row) => row.id,
	get data() {
		return data.value;
	},
	get columns() {
		return columns.value;
	},
	get rowCount() {
		return rowCount.value;
	},
	state: {
		get sorting() {
			return sorting.value;
		},
		get columnOrder() {
			return columnOrder.value;
		},
		get columnPinning() {
			return columnPinning.value;
		},
		get columnVisibility() {
			return columnVisibility.value;
		},
		get pagination() {
			return pagination.value;
		},
		get rowSelection() {
			return rowSelection.value;
		},
	},
	onSortingChange(updater) {
		const newSorting = functionalUpdate(updater, sorting.value);

		fetch({
			sorting: newSorting,
		});
	},
	onPaginationChange(updater) {
		const newPagination = functionalUpdate(updater, pagination.value);

		fetch({
			// +1 is neccessary because someone decide to index the original table from 1.
			// This will be removed once the page replaces the existing booklist page.
			page: newPagination.pageIndex + 1,
			rowsPerPage: newPagination.pageSize,
		});

		window.localStorage.setItem(
			"preferredTablePageSize",
			newPagination.pageSize.toString(),
		);
	},
	onColumnOrderChange(updater) {
		const newColumnOrder = functionalUpdate(updater, columnOrder.value);
		setColumnOrder(newColumnOrder);
	},
	onColumnVisibilityChange(updater) {
		const newColumnVisibility = functionalUpdate(
			updater,
			columnVisibility.value,
		);
		setColumnVisibility(newColumnVisibility);
	},
	onRowSelectionChange(updater) {
		const newRowSelection = functionalUpdate(updater, rowSelection.value);
		selectRows(newRowSelection);
	},
});

// ---- Filtering Start ----

// Filterable columns with "item." prefix - defined once to avoid duplication
const FILTERABLE_ITEM_COLUMNS = [
	"aggregated_course_code",
	"course_name",
	"book_id",
	"title",
	"author",
	"publisher",
	"edition",
	"department",
	"crn",
	"professor_full_name",
	"professor_email_address",
] as const;

const { initialFilters, saveFiltersToStorage, parseFiltersToQuery } =
	useTableFilters({
		localStorageKey: "booklistFilters",
		parsingTransformations: {
			...Object.fromEntries(
				FILTERABLE_ITEM_COLUMNS.map((col) => [`item.${col}`, col]),
			),
			"ops_fields.edition": "confirmed_edition",
		},
	});

const filterableColumns = computed<Filter[]>(() => {
	const filterableFields: Record<string, string> = {
		...Object.fromEntries(
			FILTERABLE_ITEM_COLUMNS.map((col) => [`item.${col}`, `item.${col}`]),
		),
		customer_content_type: "customer_content_type",
		"ops_fields.edition": "confirmed_edition",
		customer_status: "customer_status",
	};

	if (isAccessProgramEnabled.value) {
		filterableFields.access_program = "access_program";
	}
	if (isRequirementStatusEnabled.value) {
		filterableFields.requirement_status = "requirement_status";
	}
	const filteredColumns: Filter[] = [];

	for (const column of columns.value) {
		const field =
			typeof column.id === "string" ? filterableFields[column.id] : null;

		if (!field) {
			continue;
		}

		filteredColumns.push({
			id: field,
			label: (column.header ?? field) as string,
			options: {
				search: "server",
				type: "multi",
				fetch: (query?: string) => {
					return trpc.booklist.getFilterValues.query({ field, query });
				},
			},
		});
	}
	return filteredColumns;
});

async function handleUpdatedFilters(latestFilters: SelectedFilter[]) {
	const groupedFilters = parseFiltersToQuery(latestFilters);

	fetch({
		page: 1,
		rowsPerPage: Number(
			globalThis.window.localStorage?.preferredTablePageSize ?? 25,
		),
		filters: {
			...groupedFilters,
			startDate: academicDatesStore.selectedDates?.startDate,
			endDate: academicDatesStore.selectedDates?.endDate,
		},
	});

	// Store the filters to localstorage
	saveFiltersToStorage(latestFilters);
}
// ---- Filtering End ----

// ---- Date Filter Start ----
const academicDatesStore = useAcademicDates();
const selectedDateAfter = ref<Date>(new Date());
const selectedDateRange = ref<Date[]>(
	academicDatesStore.selectedDates
		? [
				new Date(academicDatesStore.selectedDates?.startDate),
				new Date(academicDatesStore.selectedDates?.endDate),
			]
		: [],
);
const isMultiCalendars = ref<boolean>(true);

watch(
	() => academicDatesStore.selectedDates,
	(newValue, oldValue) => {
		if (newValue?.id === oldValue?.id) {
			return;
		}

		fetch({
			page: 1,
			filters: {
				...filters.value,
				startDate: academicDatesStore.selectedDates?.startDate,
				endDate: academicDatesStore.selectedDates?.endDate,
			},
		});

		// update date picker and switch to multi calendars
		isMultiCalendars.value = true;
		selectedDateRange.value = academicDatesStore.selectedDates
			? [
					new Date(academicDatesStore.selectedDates?.startDate),
					new Date(academicDatesStore.selectedDates?.endDate),
				]
			: [];
	},
);

function handleDateRangeChanged([startDate, endDate]: [Date?, Date?]) {
	fetch({
		page: 1,
		filters: {
			...filters.value,
			startDate: startDate?.toISOString(),
			endDate: endDate?.toISOString(),
		},
	});
}

// ---- Date Filter End ----

const router = useRouter();

const titleRequestStore = useIndividualTitleRequest();
const showRequestLabelTimerReady = useTimeout(5 * 1000, {
	callback: () => {
		titleRequestStore.reset();
	},
});

function goToRequest() {
	router.push(
		getRequestPageRoute(
			institutionConfig.value.approval_config,
			isEAorIA.value,
		),
	);
}

async function downloadBooklist() {
	if (isDownloadingTable.value) {
		return;
	}
	const { startDate, endDate, ...filtersWithoutAcademicDate } = filters.value;
	const downloadWindow = window.open("", "_self");
	isDownloadingTable.value = true;
	const { downloadId } = await trpc.booklist.getDownloadLink.mutate({
		order: sorting.value?.[0]?.id
			?.replace("ops_fields.edition", "confirmed_edition")
			?.replace("item.", "") as BooklistSearchSchemaInput["order"],
		orderDirection: sorting.value?.[0]?.desc ? "desc" : "asc",
		query: searchQuery.value,
		filters: {
			...filtersWithoutAcademicDate,
			start_date: startDate,
			end_date: endDate,
		},
	});

	pubSubClient.subscribe({
		topic: {
			type: "booklist-download",
			downloadId,
		},
		callback: (event) => {
			const { url } = event.detail as { url: string };
			if (downloadWindow) {
				downloadWindow.location = url;
			}
			isDownloadingTable.value = false;
		},
	});
}

function getApprovalStatus(row: BooklistRow) {
	if (!isFeatureFlagEnabled("approvals")) {
		return row?.customer_status;
	}

	if (
		row.approval_state &&
		["auto_approved", "approved", "corrected"].includes(row.approval_state)
	) {
		return row?.customer_status;
	} else if (row.approval_state === "denied") {
		return "approval denied";
	} else if (row.approval_state === "pending") {
		return "pending approval";
	}

	return row?.customer_status;
}

function getStatusChipText(row: BooklistRow) {
	const status = getApprovalStatus(row);

	switch (status) {
		case "valid":
			return "Requested";
		default:
			return status;
	}
}

function getRowActions(cell: Cell<BooklistRow, unknown>): MenuItemData[] {
	const actions: MenuItemData[] = [
		{
			label: "Edit",
			action: () => {
				openEditDialog([cell.row.original]);
			},
		},
		{
			label: "Cancel",
			action: () => {
				openCancelDialog([cell.row.original.id], {
					title: cell.row.original.item.title ?? "Unknown Title",
					courseName: cell.row.original.item.course_name ?? "Unknown Course",
					courseCode:
						cell.row.original.item.aggregated_course_code ??
						"Unknown Course Code",
				});
			},
		},
	];
	if (metaServiceEnabled.value) {
		actions.push(
			{
				disabled: disableDownloadWayflessOrMARC(cell.row.original, "WAYFless"),
				label: "Copy WAYFless",
				action: () => {
					handleDownloadWayflessUrl(cell.row.original);
				},
				tooltip: {
					enabled: disableDownloadWayflessOrMARC(cell.row.original, "WAYFless"),
					position: "left",
					content: getToolTipMessage(
						cell.row.original.customer_status,
						cell.row.original?.wayfless?.status,
						"WAYFless URL",
					),
				},
			},
			{
				label: "Download MARC",
				action: () => {
					handleDownloadMARCRecord(cell.row.original);
				},
				disabled: disableDownloadWayflessOrMARC(cell.row.original, "MARC"),
				tooltip: {
					enabled: disableDownloadWayflessOrMARC(cell.row.original, "MARC"),
					position: "left",
					content: getToolTipMessage(
						cell.row.original.customer_status,
						cell.row.original?.marcStatus,
						"MARC record",
					),
				},
			},
		);
	}
	return actions;
}

function getToolTipMessage(
	customerStatus: BooklistRow["customer_status"],
	status: string | undefined,
	recordType: string,
): string {
	return match({
		isPending: [
			"Cancelled",
			"Flagged",
			"Requested",
			"To Order",
			"Pending Delivery",
		].includes(customerStatus),
		status,
	})
		.with(
			{
				isPending: true,
			},
			{
				status: P.nullish,
			},
			{
				status: "pending",
			},
			() => {
				return `The ${recordType} will be available for download once this title is confirmed to be available or delivered.`;
			},
		)
		.otherwise(() => {
			return `The ${recordType} is not available for this title`;
		});
}

// Only allow download of WAYFless or MARC records if the booklist row is delivered. E-2033 and E-2034
function disableDownloadWayflessOrMARC(
	selectedRow: BooklistRow,
	recordType: "MARC" | "WAYFless",
) {
	if (selectedRow.customer_status !== "Delivered") {
		return true;
	}

	return match({
		recordType,
		marcStatus: selectedRow.marcStatus,
		wayflessLocation: selectedRow.wayfless?.location,
	})
		.with(
			{
				recordType: "MARC",
				marcStatus: P.not("ready"),
			},
			() => true,
		)
		.with(
			{
				recordType: "WAYFless",
				wayflessLocation: P.nullish,
			},
			() => true,
		)
		.otherwise(() => false);
}

async function handleDownloadWayflessUrl(selectedRow: BooklistRow) {
	if (selectedRow.wayfless) {
		navigator.clipboard.writeText(selectedRow.wayfless.location);
		toastMessageActorV5.send({
			type: "ADD_TOAST",
			toast: {
				type: "success",
				message: "WAYFless URL copied to clipboard",
			},
		});
	}
}

async function handleDownloadMARCRecord(selectedRow: BooklistRow) {
	if (!selectedRow.ops_fields.nbsi) {
		return;
	}

	toastMessageActorV5.send({
		type: "ADD_TOAST",
		toast: {
			type: "success",
			message: "Downloading MARC record",
		},
	});

	const response = await trpc.meta.downloadMarcUrl.query({
		nbsi: selectedRow.ops_fields.nbsi,
	});

	const link = document.createElement("a");
	link.href = response.url;
	link.setAttribute("download", `${selectedRow.ops_fields.nbsi}.mrc`);

	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

const { metaServiceEnabled } = useMetaService();

function isStatusTooltipEnabled(row: BooklistRow) {
	const statusText = getApprovalStatus(row);
	return (
		(statusText === "approval denied" &&
			row.deny_reason != null &&
			row.deny_reason !== "") ||
		(statusText === "Unavailable" &&
			row.item_state.update_reason != null &&
			row.item_state.update_reason !== "")
	);
}

function getTooltipContent(row: BooklistRow) {
	const statusText = getStatusChipText(row);
	if (statusText === "approval denied") {
		return row.deny_reason;
	} else if (statusText === "Unavailable") {
		return row.item_state.update_reason;
	}
}

definePage({
	meta: {
		requiresAuth: true,
		permission: "booklist:view",
	},
});
</script>

<style scoped>
.lbl-succeed {
    background-color: #b6f7cf;
    box-shadow: 0 0 5px #a0dab6;
}

.info-icon-warning {
    fill: #A56200 !important;
    stroke: #A56200 !important;
    color: #A56200 !important;
}

.info-icon-warning * {
    fill: #A56200 !important;
    stroke: #A56200 !important;
    color: #A56200 !important;
}
</style>
